import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useAuth } from '@/contexts/AuthContext';
import {
  useModelBySlug,
  useModelVersions,
  useTrackView,
  useRestoreVersion,
  useUploadThumbnail,
} from '@/hooks/useModels';
import { Parameter } from '@shared/types';
import { ViewerSection } from '@/components/viewer/ViewerSection';
import { ParameterSection } from '@/components/parameter/ParameterSection';
import { DownloadButton } from '@/components/parameter/DownloadButton';
import { EditorToolbar } from '@/components/editor/EditorToolbar';
import { ShareDialog } from '@/components/share/ShareDialog';
import { VersionHistory } from '@/components/version/VersionHistory';
import { CodeEditor } from '@/components/code/CodeEditor';
import { ScreenshotDialog } from '@/components/viewer/ScreenshotDialog';
import { MeshpitLogo } from '@/components/MeshpitLogo';
import { BlobProvider } from '@/contexts/BlobContext';
import { ColorProvider } from '@/contexts/ColorContext';
import {
  SceneSettingsProvider,
  useSceneSettings,
} from '@/contexts/SceneSettingsContext';
import { CommandPalette } from '@/components/palette/CommandPalette';
import { useCommandRegistry } from '@/commands/useCommandRegistry';
import { useRecentCommands } from '@/hooks/useRecentCommands';
import {
  DEFAULT_VIEWER_STATE,
  type ViewerState,
} from '@/components/viewer/toolbar/ViewerToolbar';
import { useBlob } from '@/contexts/BlobContext';
import type { CodeEditorRef } from '@/components/code/CodeEditor';
import {
  updateParameter,
  generateDisplayName,
  parseParameters,
} from '@/utils/parameterUtils';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronUp, GitFork, Eye, Code, Loader2, Wrench } from 'lucide-react';
import type { ModelVersion } from '@/lib/supabase/types';
import { McpTools } from '@/mcp/McpTools';

// Inner component with access to SceneSettingsContext
function PublicModelCommandPaletteInner({
  open,
  onOpenChange,
  editorRef,
  onFork,
  onShare,
  onScreenshot,
  onShowHistory,
  isOwner,
  hasModel,
  viewerState,
  onViewerStateChange,
  parameters,
  onParametersChange,
  scadCode,
  recents,
  onRecordUsed,
  modelTitle,
  isPublic,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editorRef: React.RefObject<CodeEditorRef | null>;
  onFork?: () => void;
  onShare?: () => void;
  onScreenshot?: () => void;
  onShowHistory?: () => void;
  isOwner?: boolean;
  hasModel?: boolean;
  viewerState: ViewerState;
  onViewerStateChange: (updater: (prev: ViewerState) => ViewerState) => void;
  parameters: Parameter[];
  onParametersChange: (params: Parameter[]) => void;
  scadCode?: string;
  recents: string[];
  onRecordUsed: (id: string) => void;
  modelTitle?: string;
  isPublic?: boolean;
}) {
  const { settings, updateSSAO, updateBloom, updateShadow } =
    useSceneSettings();
  const { blob: stlBlob } = useBlob();

  const commands = useCommandRegistry({
    editorRef,
    onFork,
    onShare,
    onScreenshot,
    onShowHistory,
    isOwner,
    hasModel,
    viewerState,
    onViewerStateChange,
    ssaoEnabled: settings.ssao.enabled,
    onSSAOToggle: (enabled) => updateSSAO({ enabled }),
    bloomEnabled: settings.bloom.enabled,
    onBloomToggle: (enabled) => updateBloom({ enabled }),
    softShadowsEnabled: settings.shadow.soft && settings.shadow.enabled,
    onSoftShadowsToggle: (enabled) =>
      updateShadow({ enabled: true, soft: enabled }),
    parameters,
    onParametersChange,
    stlBlob: stlBlob ?? null,
    scadCode,
  });

  return (
    <>
      <CommandPalette
        open={open}
        onOpenChange={onOpenChange}
        commands={commands}
        recents={recents}
        onRecordUsed={onRecordUsed}
      />
      <McpTools
        commands={commands}
        scadCode={scadCode ?? ''}
        onSetCode={() => {
          // Read-only for non-owners; owners use the editor directly
        }}
        parameters={parameters}
        onParametersChange={onParametersChange}
        viewerState={viewerState}
        modelInfo={{
          title: modelTitle ?? 'Untitled Model',
          isPublic: isPublic ?? true,
          isDirty: false,
          lastSavedAt: null,
          hasModel: hasModel ?? false,
        }}
      />
    </>
  );
}

export function PublicModelView() {
  const { username, slug } = useParams<{ username: string; slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile: currentUser, isAuthenticated } = useAuth();

  // Fetch model data
  const {
    data: model,
    isLoading: isLoadingModel,
    isError,
  } = useModelBySlug(username || null, slug || null);

  // Fetch version history
  const { data: versions = [] } = useModelVersions(model?.id || null);

  // Track view mutation
  const trackView = useTrackView();

  // Restore version mutation
  const restoreVersionMutation = useRestoreVersion();

  // Thumbnail upload
  const uploadThumbnail = useUploadThumbnail();

  // Local state
  const [scadCode, setScadCode] = useState('');
  const [parameters, setParameters] = useState<Parameter[]>([]);

  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [showScreenshotDialog, setShowScreenshotDialog] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  // Command palette state
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const { recents, recordUsed } = useRecentCommands();

  // Viewer state lifted for command palette
  const [viewerState, setViewerState] =
    useState<ViewerState>(DEFAULT_VIEWER_STATE);
  const handleFitToBoundsReady = useCallback((fitFn: () => void) => {
    // Stored in ref to avoid triggering re-renders
    fitToBoundsRef.current = fitFn;
  }, []);
  const fitToBoundsRef = useRef<(() => void) | null>(null);

  // Dummy editor ref for public view (no editor in most modes)
  const editorRef = useRef<CodeEditorRef | null>(null);

  const isOwner = currentUser?.id === model?.user_id;
  const isEmbed = searchParams.get('embed') === 'true';
  const isShowcase = searchParams.get('mode') === 'showcase';

  // Product mode: default ON for visitors, OFF for owners.
  // ownerModeInitializedRef ensures the default is only applied once when
  // ownership resolves (model loads async), preserving user toggles thereafter.
  const ownerModeInitializedRef = useRef(false);
  const [isProductMode, setIsProductMode] = useState(true);
  if (isOwner && !ownerModeInitializedRef.current) {
    ownerModeInitializedRef.current = true;
    // Calling setState during render is safe when guarded by a ref; React
    // re-renders synchronously with the updated state, no extra cycle needed.
    setIsProductMode(false);
  }

  const isUiUpdateRef = useRef(false);

  const handleCodeChange = useCallback((value: string | undefined) => {
    if (value !== undefined) {
      setScadCode(value);
      if (!isUiUpdateRef.current) {
        setParameters(parseParameters(value));
      }
    }
  }, []);
  useEffect(() => {
    if (model) {
      setScadCode(model.scad_code);
      setParameters(parseParameters(model.scad_code));
      setThumbnailUrl(model.thumbnail_url);

      // Track view (once per session)
      if (!hasTrackedView && !isOwner) {
        trackView.mutate(model.id);
        setHasTrackedView(true);
      }
    }
  }, [model, isOwner, hasTrackedView, trackView]);

  // Cmd+K shortcut for command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleParametersChange = useCallback(
    (newParams: Parameter[]) => {
      let newCode = scadCode;
      newParams.forEach((param) => {
        newCode = updateParameter(newCode, param);
      });

      isUiUpdateRef.current = true;
      setScadCode(newCode);
      setParameters(newParams);
      // We rely on a timeout to reset this if we expect Monaco's onChange to fire asynchronously
      // But in PublicModelView, there is no setValueWithUndo. We just change scadCode state,
      // which causes CodeEditor to re-render. CodeEditor has an internal effect that updates
      // the model value if the prop changes. This triggers onChange asynchronously.
      setTimeout(() => {
        isUiUpdateRef.current = false;
      }, 50);
    },
    [scadCode],
  );

  const handleSetThumbnail = useCallback(
    (blob: Blob) => {
      if (!model) return;
      uploadThumbnail.mutate(
        { modelId: model.id, thumbnailBlob: blob },
        {
          onSuccess: (url) => {
            if (url) setThumbnailUrl(url);
          },
        },
      );
    },
    [model, uploadThumbnail],
  );

  const handleFork = () => {
    if (!isAuthenticated) {
      // TODO: Show login dialog
      return;
    }
    navigate('/new', { state: { forkFrom: model } });
  };

  const handlePreviewVersion = (version: ModelVersion) => {
    setScadCode(version.scad_code);
    setParameters(parseParameters(version.scad_code));
  };

  const handleRestoreVersion = async (version: ModelVersion) => {
    if (!model) return;
    await restoreVersionMutation.mutateAsync({
      modelId: model.id,
      version,
    });
  };

  // Loading state
  if (isLoadingModel) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Model not found
  if (isError || !model) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-foreground">
            Model Not Found
          </h1>
          <p className="mt-2 text-muted-foreground">
            This model doesn't exist or has been deleted.
          </p>
          <Button onClick={() => navigate('/explore')} className="mt-4">
            Explore Models
          </Button>
        </div>
      </div>
    );
  }

  // Embed mode - simplified view
  if (isEmbed) {
    return (
      <BlobProvider>
        <ColorProvider>
          <SceneSettingsProvider>
            <div className="flex h-full w-full overflow-hidden bg-background">
              <PanelGroup direction="horizontal">
                <Panel minSize={50}>
                  <ViewerSection scadCode={scadCode} parameters={parameters} />
                </Panel>
                <PanelResizeHandle className="resize-handle" />
                <Panel defaultSize={30} minSize={20}>
                  <ParameterSection
                    parameters={parameters}
                    onParametersChange={handleParametersChange}
                    scadCode={scadCode}
                  />
                </Panel>
              </PanelGroup>
            </div>
          </SceneSettingsProvider>
        </ColorProvider>
      </BlobProvider>
    );
  }

  // Showcase mode — bare full-screen viewer, no UI whatsoever
  if (isShowcase) {
    return (
      <BlobProvider>
        <ColorProvider>
          <SceneSettingsProvider>
            <div className="h-full w-full overflow-hidden">
              <ViewerSection
                scadCode={scadCode}
                showcaseMode
                parameters={parameters}
              />
            </div>
          </SceneSettingsProvider>
        </ColorProvider>
      </BlobProvider>
    );
  }

  // Product mode layout — full-screen preview with overlays
  const productModeLayout = (
    <div className="relative h-full w-full overflow-hidden">
      {/* Full-screen preview — offset center when parameter panel is visible */}
      <div className="absolute inset-0">
        <ViewerSection
          scadCode={scadCode}
          productMode
          cameraOffsetX={parameters.length > 0 ? 168 : 0}
          parameters={parameters}
          viewerState={viewerState}
          onViewerStateChange={setViewerState}
          onFitToBoundsReady={handleFitToBoundsReady}
        />
      </div>

      {/* Logo overlay — top left */}
      <div className="pointer-events-none absolute left-4 top-4 z-10 flex items-center gap-2">
        <MeshpitLogo className="pointer-events-auto h-8 w-8 text-foreground drop-shadow-md" />
        <span className="pointer-events-auto text-lg font-semibold text-foreground drop-shadow-md">
          Meshpit
        </span>
      </div>

      {/* Model title + author */}
      <div className="pointer-events-none absolute left-16 top-5 z-10">
        <span className="text-sm font-medium text-foreground drop-shadow-md">
          {model.title}
        </span>
        {model.author.username && (
          <span className="ml-2 text-xs text-muted-foreground drop-shadow-md">
            by @{model.author.username}
          </span>
        )}
      </div>

      {/* Parameters overlay — right side (w-80 = 320px + right-4 = 16px gap) */}
      {parameters.length > 0 && (
        <div className="absolute bottom-4 right-4 top-4 z-10 flex w-80 flex-col overflow-hidden rounded-lg border border-border/50 bg-background/80 shadow-lg backdrop-blur-md">
          <ParameterSection
            parameters={parameters}
            onParametersChange={handleParametersChange}
            scadCode={scadCode}
            productMode
          />
        </div>
      )}

      {/* Download button overlay — bottom left */}
      <div className="absolute bottom-4 left-4 z-10">
        <DownloadButton
          scadCode={scadCode}
          className="w-48 drop-shadow-md"
          parameters={parameters}
        />
      </div>

      {/* Model stats — above download button */}
      <div className="absolute bottom-[4.5rem] left-4 z-10 flex items-center gap-3 rounded-md border border-border/50 bg-background/80 px-3 py-1.5 text-xs text-muted-foreground backdrop-blur-sm">
        <span className="flex items-center gap-1">
          <Eye className="h-3.5 w-3.5" />
          {model.view_count} views
        </span>
        <span className="flex items-center gap-1">
          <GitFork className="h-3.5 w-3.5" />
          {model.fork_count || 0} forks
        </span>
      </div>

      {/* Switch to Dev Mode button (for owners) / View Code (for visitors) */}
      <div className="absolute left-4 top-14 z-10 flex items-center gap-2">
        {isOwner && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsProductMode(false)}
            className="gap-1.5 bg-background/80 backdrop-blur-sm"
          >
            <Wrench className="h-3.5 w-3.5" />
            Dev Mode
          </Button>
        )}
        {!isOwner && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsProductMode(false)}
            className="gap-1.5 bg-background/80 backdrop-blur-sm"
          >
            <Code className="h-3.5 w-3.5" />
            Details
          </Button>
        )}
      </div>
    </div>
  );

  // Dev mode layout — classic panel view
  const devModeLayout = (
    <div className="flex h-full w-full flex-col overflow-hidden bg-background">
      {/* Toolbar */}
      <EditorToolbar
        title={model.title}
        onTitleChange={() => {}}
        isPublic={model.is_public}
        onVisibilityChange={() => {}}
        onSave={() => {}}
        onFork={handleFork}
        onShare={() => setShowShareDialog(true)}
        onShowHistory={isOwner ? () => setShowVersionHistory(true) : undefined}
        isOwner={isOwner}
        modelSlug={model.slug}
        onScreenshot={() => setShowScreenshotDialog(true)}
        thumbnailUrl={thumbnailUrl}
        authorUsername={model.author.username}
        onProductMode={() => setIsProductMode(true)}
      />

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        <PanelGroup direction="horizontal">
          {/* Code Panel (collapsible for non-owners) */}
          {(isOwner || showCode) && (
            <>
              <Panel
                defaultSize={isOwner ? 30 : 25}
                minSize={15}
                collapsible={true}
              >
                <div className="flex h-full flex-col border-r border-border">
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <span className="text-sm font-medium text-foreground">
                      {isOwner ? 'SCAD Editor' : 'Source Code'}
                    </span>
                    {!isOwner && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCode(false)}
                        className="h-6 px-2 text-xs"
                      >
                        Hide
                      </Button>
                    )}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <CodeEditor
                      value={scadCode}
                      onChange={isOwner ? handleCodeChange : () => {}}
                    />
                  </div>
                </div>
              </Panel>
              <PanelResizeHandle className="resize-handle" />
            </>
          )}

          {/* Viewer Panel */}
          <Panel minSize={30}>
            <div className="relative h-full">
              <ViewerSection
                scadCode={scadCode}
                parameters={parameters}
                viewerState={viewerState}
                onViewerStateChange={setViewerState}
                onFitToBoundsReady={handleFitToBoundsReady}
              />

              {/* View Code button (for non-owners when code is hidden) */}
              {!isOwner && !showCode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCode(true)}
                  className="absolute left-4 top-4 gap-1.5"
                >
                  <Code className="h-4 w-4" />
                  View Code
                </Button>
              )}

              {/* Model stats overlay */}
              <div className="absolute bottom-4 left-4 flex items-center gap-3 rounded-md border border-border bg-background/90 px-3 py-2 text-xs text-muted-foreground backdrop-blur-sm">
                <span className="flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" />
                  {model.view_count} views
                </span>
                <span className="flex items-center gap-1">
                  <GitFork className="h-3.5 w-3.5" />
                  {model.fork_count || 0} forks
                </span>
              </div>
            </div>
          </Panel>

          <PanelResizeHandle className="resize-handle" />

          {/* Parameters Panel */}
          <Panel defaultSize={25} minSize={20} collapsible={true}>
            <ParameterSection
              parameters={parameters}
              onParametersChange={handleParametersChange}
              scadCode={scadCode}
            />
          </Panel>
        </PanelGroup>
      </div>

      {/* Description (collapsible at bottom) */}
      {model.description && (
        <Collapsible>
          <div className="border-t border-border">
            <CollapsibleTrigger asChild>
              <button className="flex w-full items-center justify-between px-4 py-2 text-sm text-muted-foreground hover:bg-accent">
                <span>Description</span>
                <ChevronUp className="h-4 w-4 transition-transform [[data-state=open]_&]:rotate-180" />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 pb-4 text-sm text-foreground">
                {model.description}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}
    </div>
  );

  return (
    <BlobProvider>
      <ColorProvider>
        <SceneSettingsProvider>
          {isProductMode ? productModeLayout : devModeLayout}

          {/* Dialogs */}
          <ScreenshotDialog
            open={showScreenshotDialog}
            onOpenChange={setShowScreenshotDialog}
            onSetThumbnail={isOwner ? handleSetThumbnail : undefined}
            canSetThumbnail={isOwner}
          />

          <ShareDialog
            open={showShareDialog}
            onOpenChange={setShowShareDialog}
            modelUrl={`/${model.author.username}/${model.slug}`}
            modelTitle={model.title}
            authorUsername={model.author.username}
          />

          <VersionHistory
            open={showVersionHistory}
            onOpenChange={setShowVersionHistory}
            versions={versions}
            currentVersionId={model.current_version_id}
            onPreviewVersion={handlePreviewVersion}
            onRestoreVersion={handleRestoreVersion}
            isRestoring={restoreVersionMutation.isPending}
          />

          {/* Command Palette */}
          <PublicModelCommandPaletteInner
            open={showCommandPalette}
            onOpenChange={setShowCommandPalette}
            editorRef={editorRef}
            onFork={handleFork}
            onShare={() => setShowShareDialog(true)}
            onScreenshot={() => setShowScreenshotDialog(true)}
            onShowHistory={
              isOwner ? () => setShowVersionHistory(true) : undefined
            }
            isOwner={isOwner}
            hasModel={!!model}
            viewerState={viewerState}
            onViewerStateChange={setViewerState}
            parameters={parameters}
            onParametersChange={handleParametersChange}
            scadCode={scadCode}
            recents={recents}
            onRecordUsed={recordUsed}
            modelTitle={model?.title}
            isPublic={model?.is_public}
          />
        </SceneSettingsProvider>
      </ColorProvider>
    </BlobProvider>
  );
}
